<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-start justify-between gap-3">
      <div class="min-w-0">
        <p class="text-sm font-semibold text-gray-900 dark:text-white">{{ title }}</p>
        <p v-if="subtitle" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{{ subtitle }}</p>
      </div>
      <div class="flex items-center gap-2 flex-shrink-0">
        <slot name="legend" />
        <!-- Three widgets on this page describe the present rather than the
             selected window. Saying so is what stops someone changing the
             range, seeing this not move, and filing it as a bug. -->
        <span
          v-if="asOfNow"
          class="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 whitespace-nowrap"
        >as of now</span>
      </div>
    </div>

    <!-- Skeleton mirrors the real chart's height and shape rather than being a
         grey rectangle, per CLAUDE.md's design system. -->
    <div v-if="loading" class="animate-pulse p-5 flex flex-col justify-end gap-2" :style="{ height: `${height}px` }">
      <div class="flex items-end gap-1.5 flex-1">
        <div
          v-for="i in 12" :key="i"
          class="flex-1 bg-gray-100 dark:bg-gray-800 rounded-t"
          :style="{ height: `${30 + Math.abs(Math.sin(i * 1.7)) * 60}%` }"
        ></div>
      </div>
      <div class="h-2 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
    </div>

    <div v-else-if="empty" class="flex flex-col items-center justify-center px-4 text-center" :style="{ height: `${height}px` }">
      <div class="w-11 h-11 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625Z"/>
        </svg>
      </div>
      <p class="text-sm font-medium text-gray-600 dark:text-gray-300">{{ emptyTitle }}</p>
      <p v-if="emptyHint" class="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-xs">{{ emptyHint }}</p>
    </div>

    <div v-else><slot /></div>
  </div>
</template>

<script setup>
// One place for the card chrome every stats widget shares: header, skeleton,
// empty state. Without it each chart reimplements three states slightly
// differently and the page stops feeling like one page.
defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  empty: { type: Boolean, default: false },
  emptyTitle: { type: String, default: 'Nothing to show yet' },
  emptyHint: { type: String, default: '' },
  // Marks a widget as describing the present rather than the selected range.
  asOfNow: { type: Boolean, default: false },
  height: { type: Number, default: 256 },
})
</script>
